import React, {useState, Dispatch} from 'react';
import {updatePatientById} from '../../api/patients';
import {Button, ButtonWithLoader} from '../common/Button';
import {
  phoneNumberReplaceRegex
} from '../../constants/constants';
import {Form, Formik, FormikHelpers} from 'formik';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import RequestError from '../../api/RequestError';
import {PatientListProps} from '../../constants/types';
import EnsurePermission from '../common/EnsurePermission';
import permissionsInfo from '../../constants/permissionsInfo';
import ActionButtons from '../common/layout/ActionButtons';
import {patientValidationSchema} from '../../constants/validationSchemes';
import {useHistory, useLocation} from 'react-router-dom';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {useTranslation} from 'react-i18next';
import {FormGroup} from '@mui/material';
import {TextField} from '../common/formik/fields';
import PhoneInput from '../common/formik/inputs/PhoneInput';
import {useSnackbarHook} from '../../hooks/useSnackbar';
import {useAppSelector} from '../../store/hooks';

type PatientEditProfileProps = {
  id: string,
  apiData: PatientListProps,
  setPatientName: Dispatch<string>
}

type LocationState = {
  from?: string,
  callSheetsPathMemo?: string
}

const Profile = (
    {id, apiData, setPatientName}: PatientEditProfileProps
) => {
  const {t} = useTranslation('patients');
  const {state} = useLocation<LocationState>();
  const {from} = state || {from: ''};
  const {callSheetsPathMemo} = state || {callSheetsPathMemo: ''};
  const history = useHistory();
  const [apiValidationError, setApiValidationError] =
    useState<Nullable<Dictionary<string[]>>>(null);
  const {permissions: currentUserPermissions} = useAppSelector((state) =>
    state.permissions);
  const noEditPermission = !permissionsInfo.patients.edit.some((permission) =>
    currentUserPermissions.includes(permission));
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const clearMessages = () => {
    setApiValidationError(null);
  };

  const initialValues = {
    id: apiData?.id,
    firstName: apiData?.firstName,
    lastName: apiData?.lastName,
    phoneNumber: apiData?.phoneNumber?.replace(
        /([0-9]{1,3})([0-9]{3})([0-9]{4}$)/,
        '($1) $2-$3'
    ) || '',
    patientId: apiData?.patientId || '',
    senecaPatientId: apiData?.id.toString() || ''
  };

  const onSubmit = async (
      values: PatientListProps,
      actions: FormikHelpers<PatientListProps>
  ) => {
    values.phoneNumber =
      values.phoneNumber.replace(phoneNumberReplaceRegex, '');
    const sanitizeValues = sanitize(values);

    try {
      await updatePatientById(id, sanitizeValues);
      setPatientName(`${sanitizeValues?.firstName }` +
        ` ${sanitizeValues?.lastName}`);
      actions.resetForm({values: values});
      addSuccessToast(t('toasts.updatePatientMessage'));
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'patient');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  return (
    <div className="tab-content">
      <Formik
        initialValues={initialValues}
        validationSchema={patientValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({setFieldTouched, submitForm, values,
          isSubmitting, dirty, isValid}) => {
          useOnUnsavedChanges(dirty);

          return (
            <>
              <Form className="form">
                <FormGroup>
                  <TextField
                    label="Patient First Name"
                    id="patientFirstName"
                    name="firstName"
                    required
                    disabled={noEditPermission || isSubmitting}
                    apiValidationError={apiValidationError}
                    onFocus={() => {
                      setApiValidationError(null);
                    }}
                    onBlur={() => {
                      setFieldTouched('firstName', true);
                      setApiValidationError(null);
                      clearMessages();
                    }}
                  />
                </FormGroup>

                <FormGroup>
                  <TextField
                    label="Patient Last Name"
                    id="patientLastName"
                    name="lastName"
                    required
                    disabled={noEditPermission || isSubmitting}
                    apiValidationError={apiValidationError}
                    onFocus={() => {
                      setApiValidationError(null);
                    }}
                    onBlur={() => {
                      setFieldTouched('lastName', true);
                      setApiValidationError(null);
                      clearMessages();
                    }}
                  />
                </FormGroup>

                <FormGroup>
                  <TextField
                    onFocus={() => {
                      setApiValidationError(null);
                    }}
                    onBlur={() => {
                      setFieldTouched('phoneNumber', true);
                      setApiValidationError(null);
                      clearMessages();
                    }}
                    label="Cell phone number"
                    name="phoneNumber"
                    id="patientPhoneNumber"
                    inputLabelProps={{shrink: !!values?.phoneNumber}}
                    customInput={{
                      inputComponent: PhoneInput
                    }}
                    apiValidationError={apiValidationError}
                    disabled={noEditPermission || isSubmitting}
                  />
                </FormGroup>

                <FormGroup>
                  <TextField
                    label="Seneca Patient ID"
                    id="senecaPatientId"
                    name="senecaPatientId"
                    disabled
                  />
                </FormGroup>

                <FormGroup>
                  <TextField
                    label="Patient ID"
                    onFocus={() => {
                      setApiValidationError(null);
                    }}
                    onBlur={() => {
                      setFieldTouched('patientId', true);
                      setApiValidationError(null);
                      clearMessages();
                    }}
                    id="patientId"
                    name="patientId"
                    disabled={noEditPermission || isSubmitting}
                  />
                </FormGroup>
              </Form>

              <ActionButtons>
                <EnsurePermission
                  elementPermissions={permissionsInfo.patients.edit}>
                  <>
                    <ButtonWithLoader
                      type="submit"
                      testId="update-patient"
                      disabled={!(isValid && dirty)}
                      isSubmitting={isSubmitting}
                      label="Update patient"
                      onClick={submitForm}
                    />
                    {from && (
                      <Button
                        label="Back"
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                          history.push({
                            pathname: from,
                            state: {callSheetsPathMemo}
                          });
                        }}
                      />
                    )}
                  </>
                </EnsurePermission>
              </ActionButtons>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default Profile;
